import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useMenu, groupByCategory } from "../hooks/useMenu";
import { useImages } from "../hooks/useImages";

const MenuPreviewRow = ({ item, lang }) => (
  <div className="flex items-start gap-3 py-4 border-b border-[color:var(--line)]" data-testid="menu-preview-item">
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline gap-3">
        <h4 className="font-serif-display text-2xl truncate">
          {item[`name_${lang}`] || item.name_it}
        </h4>
        <span className="divider-dotted hidden sm:block" />
        <span className="font-serif-display text-xl text-[color:var(--wine)] whitespace-nowrap">
          {item.price}
        </span>
      </div>
      <p className="mt-1 text-sm text-[color:var(--ink-3)] leading-relaxed">
        {item[`desc_${lang}`] || item.desc_it}
      </p>
    </div>
  </div>
);

const MenuHighlights = () => {
  const { t, lang } = useLang();
  const { items, loading } = useMenu();
  const { one } = useImages();
  const grouped = groupByCategory(items);
  const dish1 = one("menu_dish1", "");
  const dish2 = one("menu_dish2", "");

  const featured = [
    { key: "coffee", label: t.menu.categories.coffee, items: grouped.coffee.slice(0, 3) },
    { key: "lunch", label: t.menu.categories.lunch_dinner, items: grouped.lunch.slice(0, 3) },
  ];

  return (
    <section data-testid="menu-highlights-section" className="py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 lg:gap-12 mb-16">
          <div className="col-span-12 md:col-span-7">
            <p className="overline text-[color:var(--wine)] flex items-center">
              <span className="section-line"></span>
              {t.menu.eyebrow}
            </p>
            <h2 className="mt-6 font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              {t.menu.title}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:pt-4 flex flex-col justify-between">
            <p className="text-lg text-[color:var(--ink-2)] leading-relaxed">
              {t.menu.subtitle}
            </p>
            <div className="mt-6">
              <Link
                to="/menu"
                data-testid="menu-view-all"
                className="btn-ghost"
              >
                {t.menu.view_all}
                <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12">
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {loading ? (
              <p className="text-[color:var(--ink-3)] md:col-span-2">…</p>
            ) : (
              featured.map((cat) => (
                <div key={cat.key} data-testid={`menu-cat-${cat.key}`}>
                  <p className="overline text-[color:var(--wine)] mb-4 flex items-center">
                    <span className="section-line"></span>
                    {cat.label}
                  </p>
                  <div>
                    {cat.items.map((item) => (
                      <MenuPreviewRow key={item.id} item={item} lang={lang} />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-4">
              {dish1 && (
                <div className="aspect-[3/4] overflow-hidden group mt-12" data-testid="menu-dish1-image">
                  <img
                    src={dish1}
                    alt="Piatto italiano"
                    className="w-full h-full object-cover smooth-image"
                  />
                </div>
              )}
              {dish2 && (
                <div className="aspect-[3/4] overflow-hidden group" data-testid="menu-dish2-image">
                  <img
                    src={dish2}
                    alt="Specialità"
                    className="w-full h-full object-cover smooth-image"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MenuHighlights;
